/**
 * User Page Setup Defaults Service
 * Manages user-specific page setup preferences with scope support
 */

export type ScopeType = 'global' | 'matter' | 'client';

export interface UserDefaultsScope {
  type: ScopeType;
  id?: string;
}

export interface TypographyDefaults {
  fontFamily?: string;
  fontSize?: number;
  fontWeight?: number;
  fontStyle?: 'normal' | 'italic' | 'oblique';
  lineHeight?: number;
}

export interface PageDefaults {
  pageSize?: string;
  dpi?: number;
  orientation?: 'portrait' | 'landscape';
  columns?: number;
  hyphenation?: boolean;
  bidi?: 'auto' | 'ltr' | 'rtl';
}

export interface MarginDefaults {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface UserPageSetupDefaults {
  id: string;
  userId: string;
  realmId: string;
  scope: UserDefaultsScope;
  typography?: TypographyDefaults;
  page?: PageDefaults;
  margins?: MarginDefaults;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserDefaultsRequest {
  scope: UserDefaultsScope;
  typography?: TypographyDefaults;
  page?: PageDefaults;
  margins?: MarginDefaults;
}

export interface ApiResponse<T> {
  status: 'success' | 'error';
  data?: T;
  message?: string;
  count?: number;
}

export class UserPageSetupDefaultsService {
  private baseUrl = '/api/v1/users/me/page-setup-defaults';

  /**
   * Get authentication headers with Keycloak JWT token
   * Uses the same robust pattern as other working EdgeLex services
   */
  private async getAuthHeaders(): Promise<HeadersInit> {
    try {
      console.log('🔑 [UserPageSetupDefaultsService] Getting token with robust fallback detection...');
      let token = null;
      
      // 1. First try the React Keycloak provider (most reliable)
      const keycloak = (window as any).keycloak;
      if (keycloak?.token) {
        console.log('✅ [UserPageSetupDefaultsService] Found token via React Keycloak provider');
        token = keycloak.token;
      } else {
        // 2. Fall back to storage-based detection with JWT pattern matching
        const jwtPattern = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
        
        console.log('🔍 [UserPageSetupDefaultsService] Falling back to storage detection...');
        for (const storage of [sessionStorage, localStorage]) {
          for (const key of Object.keys(storage)) {
            const value = storage.getItem(key);
            if (!value) continue;
            
            // Check if value is a JWT token directly
            if (jwtPattern.test(value)) {
              console.log(`✅ [UserPageSetupDefaultsService] Found JWT token in ${storage === localStorage ? 'localStorage' : 'sessionStorage'}: ${key}`);
              token = value;
              break;
            }
            
            // Check if value is JSON containing a JWT token
            if (value.startsWith('{')) {
              try {
                const parsed = JSON.parse(value);
                const tokenFields = ['token', 'access_token', 'accessToken', 'idToken', 'id_token'];
                for (const field of tokenFields) {
                  if (parsed[field] && jwtPattern.test(parsed[field])) {
                    console.log(`✅ [UserPageSetupDefaultsService] Found JWT token in ${storage === localStorage ? 'localStorage' : 'sessionStorage'} ${key}.${field}`);
                    token = parsed[field];
                    break;
                  }
                }
              } catch (e) { /* ignore JSON parse errors */ }
            }
          }
          if (token) break;
        }
      }
      
      if (!token) {
        console.warn('❌ [UserPageSetupDefaultsService] No JWT token found for authentication');
        console.log('🔍 [UserPageSetupDefaultsService] Keycloak authenticated:', keycloak?.authenticated);
        console.log('🔍 [UserPageSetupDefaultsService] Keycloak instance:', !!keycloak);
      }

      return {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      };
    } catch (error) {
      console.warn('[UserPageSetupDefaultsService] Failed to get auth token:', error);
      return {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };
    }
  }

  /**
   * Handle API response and errors
   */
  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    if (data.status === 'error') {
      throw new Error(data.message || 'API request failed');
    }

    return data.data;
  }

  /**
   * Get user defaults with scope hierarchy
   * Priority: matter-specific > client-specific > global > null
   */
  async getUserDefaults(context?: {
    matterId?: string;
    clientId?: string;
    scopeType?: ScopeType;
  }): Promise<UserPageSetupDefaults | null> {
    try {
      const params = new URLSearchParams();
      if (context?.matterId) params.append('matterId', context.matterId);
      if (context?.clientId) params.append('clientId', context.clientId);
      if (context?.scopeType) params.append('scopeType', context.scopeType);

      const url = `${this.baseUrl}${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: await this.getAuthHeaders()
      });

      const result = await response.json();
      return result.data || null;
    } catch (error) {
      console.warn('[UserPageSetupDefaultsService] Failed to get user defaults:', error);
      return null;
    }
  }

  /**
   * Get all user defaults (all scopes)
   */
  async getAllUserDefaults(): Promise<UserPageSetupDefaults[]> {
    try {
      const response = await fetch(`${this.baseUrl}/all`, {
        method: 'GET',
        headers: await this.getAuthHeaders()
      });

      return await this.handleResponse<UserPageSetupDefaults[]>(response);
    } catch (error) {
      console.error('[UserPageSetupDefaultsService] Failed to get all user defaults:', error);
      return [];
    }
  }

  /**
   * Create or update user defaults
   */
  async upsertUserDefaults(request: CreateUserDefaultsRequest): Promise<UserPageSetupDefaults> {
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify(request)
    });

    return await this.handleResponse<UserPageSetupDefaults>(response);
  }

  /**
   * Set typography defaults (convenience method)
   */
  async setTypographyDefaults(
    scope: UserDefaultsScope,
    typography: TypographyDefaults
  ): Promise<UserPageSetupDefaults> {
    const response = await fetch(`${this.baseUrl}/typography`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({ scope, ...typography })
    });

    return await this.handleResponse<UserPageSetupDefaults>(response);
  }

  /**
   * Set page defaults (convenience method)
   */
  async setPageDefaults(
    scope: UserDefaultsScope,
    page: PageDefaults
  ): Promise<UserPageSetupDefaults> {
    const response = await fetch(`${this.baseUrl}/page`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({ scope, ...page })
    });

    return await this.handleResponse<UserPageSetupDefaults>(response);
  }

  /**
   * Set margin defaults (convenience method)
   */
  async setMarginDefaults(
    scope: UserDefaultsScope,
    margins: MarginDefaults
  ): Promise<UserPageSetupDefaults> {
    const response = await fetch(`${this.baseUrl}/margins`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({ scope, margins })
    });

    return await this.handleResponse<UserPageSetupDefaults>(response);
  }

  /**
   * Delete user defaults
   */
  async deleteUserDefaults(id: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'DELETE',
        headers: await this.getAuthHeaders()
      });

      const result = await response.json();
      return result.status === 'success';
    } catch (error) {
      console.error('[UserPageSetupDefaultsService] Failed to delete user defaults:', error);
      return false;
    }
  }

  /**
   * Delete defaults by scope
   */
  async deleteDefaultsByScope(scopeType: ScopeType, scopeId?: string): Promise<number> {
    try {
      const params = new URLSearchParams();
      if (scopeId) params.append('scopeId', scopeId);

      const url = `${this.baseUrl}/scope/${scopeType}${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: await this.getAuthHeaders()
      });

      const result = await response.json();
      return result.deletedCount || 0;
    } catch (error) {
      console.error('[UserPageSetupDefaultsService] Failed to delete defaults by scope:', error);
      return 0;
    }
  }

  /**
   * Get scope display name for UI
   */
  static getScopeDisplayName(scope: UserDefaultsScope): string {
    switch (scope.type) {
      case 'global':
        return 'All Documents';
      case 'matter':
        return 'This Matter';
      case 'client':
        return 'This Client';
      default:
        return 'Unknown Scope';
    }
  }

  /**
   * Get scope description for UI
   */
  static getScopeDescription(scope: UserDefaultsScope): string {
    switch (scope.type) {
      case 'global':
        return 'Apply to all new documents across all matters and clients';
      case 'matter':
        return 'Apply to all new documents in this matter only';
      case 'client':
        return 'Apply to all new documents for this client only';
      default:
        return '';
    }
  }
}
